import { useState } from "react";
import { Globe, Plus, RefreshCw, Trash2, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmDialog, EmptyState, Field, PageHeader, Pager, Panel, RowsSkeleton, SearchBox, StatusBadge, Tag } from "@/components/app/kit";
import { useFetch, useServerList } from "@/hooks/useServerList";
import { api, formatApiError } from "@/lib/api";
import { fmtDateTime, fmtDate } from "@/lib/i18n";

export const DomainRow = ({ d, onChange, showTenant }) => {
  const [busy, setBusy] = useState(false);
  const [del, setDel] = useState(false);
  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/platform/domains/${d.id}/verify`);
      toast[data.status === "verified" ? "success" : "warning"](data.status === "verified" ? "DNS vérifié" : "Vérification DNS non concluante");
      onChange();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    try {
      await api.delete(`/platform/domains/${d.id}`);
      toast.success("Domaine supprimé");
      setDel(false);
      onChange();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <div className="rounded-xl border border-slate-200 p-4" data-testid={`domain-row-${d.hostname}`}>
      <div className="flex flex-wrap items-center gap-3">
        <Globe className="h-4 w-4 text-slate-400" />
        <span className="font-mono text-sm font-semibold text-slate-900">{d.hostname}</span>
        <Tag tone={d.type === "custom" ? "indigo" : "sky"}>{d.type === "custom" ? "Domaine personnalisé" : "Sous-domaine"}</Tag>
        <StatusBadge status={d.status} prefix="domain" testid={`domain-status-${d.hostname}`} />
        <Tag tone={d.https_status === "active" ? "green" : "gray"}><ShieldCheck className="h-3 w-3" />HTTPS : {{ active: "certificat actif", invalid: "invalide", unavailable: "indisponible" }[d.https_status] || "non configuré"}</Tag>
        {showTenant && <span className="text-xs text-slate-500">· {d.tenant_name}</span>}
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={verify} disabled={busy} data-testid={`domain-verify-${d.hostname}`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}<span className="ml-2">Vérifier DNS</span>
          </Button>
          {d.type === "custom" && <Button size="sm" variant="ghost" onClick={() => setDel(true)} data-testid={`domain-delete-${d.hostname}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>}
        </div>
      </div>
      <p className="text-xs text-slate-400 mt-2">
        Dernière vérification : {fmtDateTime(d.last_checked_at)}
        {d.addresses?.length > 0 && ` · Résout vers ${d.addresses.join(", ")}`}
        {d.https_expires_at && ` · Certificat valable jusqu'au ${fmtDate(d.https_expires_at)}`}
      </p>
      {d.expected_records && d.status !== "verified" && (
        <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3 overflow-x-auto">
          <p className="text-xs font-semibold text-slate-600 mb-2">Enregistrements DNS attendus</p>
          {d.expected_records.map((r) => (
            <p key={r.name + r.type} className="font-mono text-[11px] text-slate-600 whitespace-nowrap">{r.type} {r.name} → {r.value || "(cible à définir dans les paramètres)"}</p>
          ))}
        </div>
      )}
      <ConfirmDialog open={del} onOpenChange={setDel} destructive onConfirm={remove} title="Supprimer ce domaine ?" description={`${d.hostname} ne sera plus rattaché au cabinet.`} confirmLabel="Supprimer" />
    </div>
  );
};

const AddDomain = ({ open, onOpenChange, onDone }) => {
  const tenants = useFetch("/platform/tenants?page_size=100");
  const [f, setF] = useState({ tenant_id: "", hostname: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/platform/domains", f);
      toast.success("Domaine ajouté — configurez le DNS puis lancez la vérification");
      onOpenChange(false);
      setF({ tenant_id: "", hostname: "" });
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-domain-dialog">
        <DialogHeader><DialogTitle>Rattacher un domaine personnalisé</DialogTitle><DialogDescription>Le domaine restera « en attente » tant que les enregistrements DNS ne sont pas vérifiés.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <Field label="Cabinet" required>
            <Select value={f.tenant_id} onValueChange={(v) => setF({ ...f, tenant_id: v })}>
              <SelectTrigger data-testid="add-domain-tenant"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
              <SelectContent>{(tenants.data?.items || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Nom de domaine" required><Input className="font-mono" placeholder="www.mon-cabinet.fr" value={f.hostname} onChange={(e) => setF({ ...f, hostname: e.target.value.trim().toLowerCase() })} data-testid="add-domain-hostname" /></Field>
        </div>
        <DialogFooter><Button onClick={submit} disabled={busy || !f.tenant_id || !f.hostname} data-testid="add-domain-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Ajouter</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Domains() {
  const list = useServerList("/platform/domains", { status: "all", type: "all" });
  const [add, setAdd] = useState(false);
  return (
    <div>
      <PageHeader eyebrow="Infrastructure" title="Domaines et sous-domaines" subtitle="Registre des accès par domaine. Un domaine n'est considéré actif qu'après vérification DNS réelle ; le certificat HTTPS est contrôlé sur le port 443."
        actions={<Button onClick={() => setAdd(true)} data-testid="domains-add-button"><Plus className="h-4 w-4 mr-2" />Ajouter un domaine</Button>} />
      <Panel>
        <div className="flex flex-col sm:flex-row gap-3 -mt-1 mb-4">
          <SearchBox value={list.filters.search} onChange={(v) => list.setFilter("search", v)} placeholder="Nom de domaine…" testid="domains-search" />
          <Select value={list.filters.status} onValueChange={(v) => list.setFilter("status", v)}>
            <SelectTrigger className="sm:w-44 bg-white" data-testid="domains-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tous les statuts</SelectItem><SelectItem value="pending">En attente</SelectItem><SelectItem value="verified">Vérifié</SelectItem><SelectItem value="failed">Échec</SelectItem></SelectContent>
          </Select>
          <Select value={list.filters.type} onValueChange={(v) => list.setFilter("type", v)}>
            <SelectTrigger className="sm:w-48 bg-white" data-testid="domains-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tous les types</SelectItem><SelectItem value="subdomain">Sous-domaines</SelectItem><SelectItem value="custom">Domaines personnalisés</SelectItem></SelectContent>
          </Select>
        </div>
        {list.loading ? <RowsSkeleton /> : list.items.length === 0 ? <EmptyState icon={Globe} title="Aucun domaine" text="Les sous-domaines sont générés à la création des cabinets." /> : (
          <div className="space-y-3">{list.items.map((d) => <DomainRow key={d.id} d={d} onChange={list.reload} showTenant />)}</div>
        )}
        <div className="-mx-5 -mb-5 mt-4"><Pager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} /></div>
      </Panel>
      <AddDomain open={add} onOpenChange={setAdd} onDone={list.reload} />
    </div>
  );
}
